import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { CONTENT, type Lang, type Copy } from "./content";
import { useContentOverrides } from "../contexts/ContentOverridesContext";
import { unflattenObject, deepMerge } from "../lib/flatten";

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: Copy };
const I18nContext = createContext<Ctx | null>(null);

const LANGS: Lang[] = ["de", "en", "fr", "es"];

const LANG_STORAGE_KEY = "jyotish-lang";

/**
 * Initial UI language.
 *
 * The site is a single-domain SPA whose canonical URL (https://jyotish-astro.com/)
 * is DECLARED German (index.html <html lang="de">, German <title>/description,
 * canonical + de/x-default hreflang). Previously this auto-detected
 * navigator.language, which meant an English-locale visitor OR CRAWLER was
 * served the English CONTENT strings (English <title>/<h1>) on the German
 * canonical URL — the DE/EN mix the SEO audit flagged. Google indexes what it
 * renders, so the German canonical page must present as German.
 *
 * Fix: default to German (the canonical language) and only switch away on an
 * EXPLICIT user signal — a `?lang=xx` query param (shareable / used by the
 * hreflang alternates) or a previously stored choice from the language
 * switcher. Browser locale alone no longer overrides the canonical language.
 */
function detectInitial(): Lang {
  if (typeof window === "undefined") return "de";
  // 1) explicit ?lang= wins (matches hreflang alternate hrefs)
  const q = new URLSearchParams(window.location.search).get("lang");
  if (q && (LANGS as string[]).includes(q.toLowerCase())) return q.toLowerCase() as Lang;
  // 2) a stored explicit choice from the LanguageSwitcher
  try {
    const stored = window.localStorage?.getItem(LANG_STORAGE_KEY);
    if (stored && (LANGS as string[]).includes(stored)) return stored as Lang;
  } catch {
    /* localStorage may be unavailable (private mode); fall through */
  }
  // 3) canonical default — NOT navigator.language (see header)
  return "de";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitial);
  // Persist explicit language choices so they survive reloads/navigation,
  // while crawlers hitting the bare canonical URL still get German.
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage?.setItem(LANG_STORAGE_KEY, l);
    } catch {
      /* ignore storage failures */
    }
  }, []);
  // Merge admin-set overrides on top of the static CONTENT for this lang.
  // ContentOverridesProvider (main.tsx) wraps I18nProvider so this hook is
  // always safe to call here. When no overrides are present (the common
  // case for non-admin visitors, or before the first admin edit), `t`
  // equals the static CONTENT[lang] with no re-allocation overhead because
  // deepMerge returns the base unchanged when the patch is empty.
  const { overrides } = useContentOverrides();
  const t = useMemo<Copy>(() => {
    const base = CONTENT[lang];
    const langOverrides = overrides[lang];
    if (!langOverrides || Object.keys(langOverrides).length === 0) return base;
    return deepMerge(base, unflattenObject(langOverrides)) as Copy;
  }, [lang, overrides]);

  // Dynamic SEO: update <html lang>, <title>, and meta description per language.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t.meta.title;
    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = attr.split("=");
        el.setAttribute(k, v.replace(/"/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta('meta[name="description"]', 'name="description"', t.meta.description);
    setMeta('meta[property="og:title"]', 'property="og:title"', t.meta.title);
    setMeta('meta[property="og:description"]', 'property="og:description"', t.meta.description);
  }, [lang, t]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export { LANGS };
